import { Standard } from 'shared/types'
import {
  getStandardsMapFromNames,
  TreeStandards,
} from 'components/tree/standardUtilities'
import { ILeaf, ILeafRequest } from 'shared/types/tree'
import { LeafTypes } from 'shared/types/ingestion'
import { bulkCreateLeaves, bulkDeleteLeaves, deleteLeaf } from '../TreeActions'
import { createLeaf } from '../TreeRequests'

/**
 * Normalizes a standard name by removing all non-alphanumeric characters (including whitespace) and converting to uppercase.
 * This is used to create consistent keys for standard comparison and mapping.
 *
 * @param standardName The standard name to normalize
 * @returns The normalized standard name (uppercase with only alphanumeric characters, no spaces)
 */
export function normalize(standardName: string): string {
  return standardName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
}

export type StandardReplacement = {
  skillLeaf: ILeaf
  currentStandard: Standard
  newStateStandards: Standard[]
}

export type StandardReplacementNoMapping = {
  skillLeaf: ILeaf
  currentStandard: Standard
}

export type StandardReplacementNoKiddomStandard = {
  skillLeaf: ILeaf
  currentStandard: Standard
  newStateStandardName: string[]
}

export type StandardAndPotentialChildren = {
  standard: string
  potentialChildren: string[]
}

export async function getStandardsToReplace(
  treeStandards: TreeStandards,
  standardsReplacementMap: Record<string, string[]>,
  state: string,
) {
  const stateStandardsMap = (
    await getStandardsMapFromNames(
      Object.values(standardsReplacementMap).flatMap((strings) => strings),
      { state },
    )
  ).standardsMap
  const currentStandardsMap = await treeStandards.standardsMap

  // Create normalized maps for consistent lookup
  const normalizedCurrentStandardsMap =
    createNormalizedStandardsMap(currentStandardsMap)

  const standardReplacementsWithNoIssues: StandardReplacement[] = []
  const standardReplacementsWithNoMapping: StandardReplacementNoMapping[] = []
  const standardReplacementsWithNoKiddomStandard: StandardReplacementNoKiddomStandard[] =
    []

  treeStandards.skillLeaves.forEach((leaf) => {
    const currStandard = currentStandardsMap[leaf.body.skillID]
    if (currStandard.state === state) return

    // Use the pre-computed normalized name from the normalized map
    const normalizedStandardName =
      normalizedCurrentStandardsMap[leaf.body.skillID].normalizedName
    const correspondingStateStandardNames =
      standardsReplacementMap[normalizedStandardName]

    // This is an error if the set of current values in replacements map does not contain this standard
    if (!correspondingStateStandardNames) {
      standardReplacementsWithNoMapping.push({
        skillLeaf: leaf,
        currentStandard: currStandard,
      })
      return
    }

    const correspondingStateStandards = correspondingStateStandardNames.map(
      (n) => stateStandardsMap[n],
    )
    // This is an error if the replacement standard does not exist within Kiddom
    if (correspondingStateStandards.some((s) => s === undefined)) {
      standardReplacementsWithNoKiddomStandard.push({
        skillLeaf: leaf,
        currentStandard: currStandard,
        newStateStandardName: correspondingStateStandardNames,
      })
      return
    }

    standardReplacementsWithNoIssues.push({
      skillLeaf: leaf,
      currentStandard: currStandard,
      newStateStandards: correspondingStateStandards,
    })
  })
  return {
    standardReplacementsWithNoIssues,
    standardReplacementsWithNoMapping,
    standardReplacementsWithNoKiddomStandard,
  }
}

export function formNewStandardLeaves(a: StandardReplacement) {
  const newLeaves = a.newStateStandards.map((s) => {
    return {
      type: LeafTypes.Skill,
      body: {
        skillID: s.standardId,
      },
    }
  })

  return {
    tree_id: a.skillLeaf.tree_id!,
    version: a.skillLeaf.version!,
    id: a.skillLeaf.parent_id!,
    leaves: newLeaves,
  }
}

export function formNewStandardLeavesMap(a: StandardReplacement[]) {
  return a.reduce(
    (acc, replacement) => {
      if (!acc[replacement.skillLeaf.parent_id!])
        acc[replacement.skillLeaf.parent_id!] = []
      const newStandards = replacement.newStateStandards.map((s) => {
        return {
          type: LeafTypes.Skill,
          body: {
            skillID: s.standardId,
          },
        }
      })
      acc[replacement.skillLeaf.parent_id!].push(...newStandards)
      return acc
    },
    {} as Record<string, ILeafRequest[]>,
  )
}

export function bulkReplaceStandards(a: StandardReplacement[]) {
  return bulkCreateLeaves({
    treeId: a[0].skillLeaf.tree_id!,
    versionId: a[0].skillLeaf.version!,
    leaves: formNewStandardLeavesMap(a),
  }).then((r) => {
    bulkDeleteLeaves({
      treeId: a[0].skillLeaf.tree_id!,
      versionId: a[0].skillLeaf.version!,
      leaves: a.map((r) => r.skillLeaf.id),
    })
  })
}

export function replaceStandards(a: StandardReplacement) {
  return createLeaf(formNewStandardLeaves(a)).then(() => {
    // only delete the existing leaf if the new leaves were created successfully
    deleteLeaf({
      leafId: a.skillLeaf.id,
      treeId: a.skillLeaf.tree_id!,
      versionId: a.skillLeaf.version!,
    })
  })
}

/**
 * Finds potential child standards in the replacement map that start with the given standard name
 * The function normalizes the input standard name and then checks for keys in the replacement map
 * that start with the normalized name.
 *
 * @param standardName The standard name to check for potential children
 * @param standardsReplacementMap The map of normalized standard names to replacement standards
 * @returns Array of potential child standard keys from the replacement map
 */
export function getPotentialChildren(
  standardName: string,
  standardsReplacementMap: Record<string, string[]>,
) {
  const normalizedName = normalize(standardName)
  return Object.keys(standardsReplacementMap).filter((k) =>
    k.startsWith(normalizedName),
  )
}

// New normalized standards types and functions

/**
 * Enhanced standard type that includes the normalized name
 */
export interface NormalizedStandard extends Standard {
  normalizedName: string
}

/**
 * Map of standard IDs to NormalizedStandard objects
 */
export type NormalizedStandardsMap = Record<string | number, NormalizedStandard>

/**
 * Creates a map of standard IDs to NormalizedStandard objects
 * Each NormalizedStandard includes the original standard with an added normalizedName field
 *
 * @param standardsMap Original map of standard IDs to Standard objects
 * @returns Map of standard IDs to NormalizedStandard objects
 */
export function createNormalizedStandardsMap(
  standardsMap: Record<string | number, Standard>,
): NormalizedStandardsMap {
  const normalizedMap: NormalizedStandardsMap = {}

  for (const [id, standard] of Object.entries(standardsMap)) {
    normalizedMap[id] = {
      ...standard,
      normalizedName: normalize(standard.standardName),
    }
  }

  return normalizedMap
}

/**
 * Creates a replacement map from a table of space or tab-delimited values
 * Each line is processed to extract values delimited by spaces or tabs
 * The first value becomes the key, and any other values on the line become values in an array
 * Before normalization, non-alphanumeric characters are trimmed from left and right
 * All values are then normalized using the normalize function
 *
 * @param table The input table as a string, with one entry per line
 * @returns An object containing the normalized map and mapping to original standard names
 */
export function getNormalizedReplacementMap(table: string): {
  normalizedMap: Record<string, string[]>
  originalValues: Record<string, string>
  originalReplacements: Record<string, string[]>
} {
  const normalizedMap: Record<string, string[]> = {}
  const originalValues: Record<string, string> = {}
  const originalReplacements: Record<string, Record<string, string>> = {}

  // Process each non-empty line
  table.split('\n').forEach((line) => {
    const trimmedLine = line.trim()
    if (trimmedLine === '') return

    // Split by spaces or tabs to get the values
    const values = trimmedLine.split(/[\s\t]+/)
    if (values.length < 2) return // Need at least a key and one replacement

    // Store the original key and replacements for display
    const originalKey = values[0]
    const originalReplacementValues = values.slice(1)

    // Process all values - trim non-alphanumeric chars from edges, then normalize
    const processedValues = values
      .map((value) => {
        const trimmed = value.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, '')
        return normalize(trimmed)
      })
      .filter((value) => value !== '') // Filter out any empty values

    // Need at least two values (key and at least one replacement)
    if (processedValues.length < 2) return

    // First value is the key, rest are replacements
    const normalizedKey = processedValues[0]
    const replacements = processedValues.slice(1)

    // Store the mapping of normalized key to original value
    originalValues[normalizedKey] = originalKey

    // Store the mapping of normalized replacements to original values
    if (!originalReplacements[normalizedKey]) {
      originalReplacements[normalizedKey] = {}
    }

    // Map each normalized replacement to its original form
    for (let i = 0; i < replacements.length; i++) {
      if (i < originalReplacementValues.length) {
        if (!originalReplacements[normalizedKey][replacements[i]]) {
          originalReplacements[normalizedKey][replacements[i]] =
            originalReplacementValues[i]
        }
      }
    }

    // Add to the map, creating a new array or appending to existing
    if (normalizedKey in normalizedMap) {
      normalizedMap[normalizedKey].push(...replacements)
    } else {
      normalizedMap[normalizedKey] = replacements
    }
  })

  // Convert the nested replacement maps to arrays that match the normalizedMap structure
  const flatOriginalReplacements: Record<string, string[]> = {}

  for (const [key, replacementMap] of Object.entries(originalReplacements)) {
    if (normalizedMap[key]) {
      flatOriginalReplacements[key] = normalizedMap[key].map(
        (normalizedReplacement) =>
          replacementMap[normalizedReplacement] || normalizedReplacement,
      )
    }
  }

  return {
    normalizedMap,
    originalValues,
    originalReplacements: flatOriginalReplacements,
  }
}
